import { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import api from '../../services/api';
import MobileAvatar from './MobileAvatar';

const HAIR = ['none', 'short', 'balding', 'curly', 'side', 'bob', 'long', 'ponytail', 'bun', 'bangs', 'wavy', 'messy'];
const FACIAL = ['none', 'stubble', 'full', 'thick', 'bushy'];
const GLASSES = ['none', 'round', 'square', 'cat'];
const HATS = ['none', 'cap', 'beanie', 'cowboy', 'sun'];
const COSTUMES = ['none', 'dino', 'robot'];
const EYES = ['happy', 'neutral', 'cool', 'surprised', 'laugh'];

const BASE = {
  skinTone: '#F0C7A0',
  hairStyle: 'messy',
  hairColor: '#5C3A21',
  facialHairStyle: 'none',
  facialHairColor: '#5C3A21',
  eyeStyle: 'happy',
  glassesStyle: 'none',
  hatStyle: 'none',
  costumeStyle: 'none',
  shirtColor: '#7EC8E3',
  backgroundColor: '#E8EBF5',
};

function options(ids) {
  return ids.map((id) => ({ label: id, value: id }));
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
      <span style={{ fontWeight: 700 }}>{label}</span>
      {children}
    </label>
  );
}

export default function AvatarEditor({ userId, avatar, name, photoUrl, onSaved, toast }) {
  const [draft, setDraft] = useState(() => {
    const raw = avatar && typeof avatar === 'object' ? avatar : {};
    const color = (key) =>
      /^#[0-9A-Fa-f]{6}$/.test(raw[key] || '') ? raw[key] : BASE[key];
    return {
      ...BASE,
      ...raw,
      skinTone: color('skinTone'),
      hairColor: color('hairColor'),
      facialHairColor: color('facialHairColor'),
      shirtColor: color('shirtColor'),
      backgroundColor: color('backgroundColor'),
    };
  });
  const [saving, setSaving] = useState(false);
  const preview = useMemo(() => ({ ...(avatar || {}), ...draft }), [avatar, draft]);

  const set = (key) => (value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/mobile_users/${userId}/avatar`, { avatar: preview });
      toast?.show({ severity: 'success', summary: 'Avatar', detail: 'Avatar actualizado' });
      await onSaved?.();
    } catch (err) {
      toast?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el avatar' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 p-3 border-round" style={{ border: '1px solid #e5e7eb' }}>
      <div className="flex align-items-center gap-3 mb-3">
        <MobileAvatar avatar={preview} name={name} photoUrl={photoUrl} size={72} />
        <div>
          <strong>Editar avatar</strong>
          <div style={{ color: '#64748b', fontSize: 12 }}>Se guarda en el perfil del usuario.</div>
        </div>
      </div>
      <div className="grid">
        <div className="col-6 md:col-4">
          <Field label="Piel">
            <input type="color" value={draft.skinTone} onChange={(e) => set('skinTone')(e.target.value)} />
          </Field>
        </div>
        <div className="col-6 md:col-4">
          <Field label="Cabello">
            <Dropdown value={draft.hairStyle} options={options(HAIR)} onChange={(e) => set('hairStyle')(e.value)} className="w-full" />
          </Field>
        </div>
        <div className="col-6 md:col-4">
          <Field label="Color de cabello">
            <input type="color" value={draft.hairColor} onChange={(e) => set('hairColor')(e.target.value)} />
          </Field>
        </div>
        <div className="col-6 md:col-4">
          <Field label="Vello facial">
            <Dropdown value={draft.facialHairStyle} options={options(FACIAL)} onChange={(e) => set('facialHairStyle')(e.value)} className="w-full" />
          </Field>
        </div>
        <div className="col-6 md:col-4">
          <Field label="Color de vello">
            <input type="color" value={draft.facialHairColor} onChange={(e) => set('facialHairColor')(e.target.value)} />
          </Field>
        </div>
        <div className="col-6 md:col-4">
          <Field label="Ojos">
            <Dropdown value={draft.eyeStyle} options={options(EYES)} onChange={(e) => set('eyeStyle')(e.value)} className="w-full" />
          </Field>
        </div>
        <div className="col-6 md:col-4">
          <Field label="Lentes">
            <Dropdown value={draft.glassesStyle} options={options(GLASSES)} onChange={(e) => set('glassesStyle')(e.value)} className="w-full" />
          </Field>
        </div>
        <div className="col-6 md:col-4">
          <Field label="Gorra">
            <Dropdown value={draft.hatStyle} options={options(HATS)} onChange={(e) => set('hatStyle')(e.value)} className="w-full" />
          </Field>
        </div>
        <div className="col-6 md:col-4">
          <Field label="Disfraz">
            <Dropdown value={draft.costumeStyle || 'none'} options={options(COSTUMES)} onChange={(e) => set('costumeStyle')(e.value)} className="w-full" />
          </Field>
        </div>
        <div className="col-6 md:col-4">
          <Field label="Playera">
            <input type="color" value={draft.shirtColor} onChange={(e) => set('shirtColor')(e.target.value)} />
          </Field>
        </div>
        <div className="col-6 md:col-4">
          <Field label="Fondo">
            <input type="color" value={draft.backgroundColor} onChange={(e) => set('backgroundColor')(e.target.value)} />
          </Field>
        </div>
      </div>
      <Button
        type="button"
        label="Guardar avatar"
        icon="pi pi-check"
        className="p-button-sm mt-3"
        loading={saving}
        onClick={save}
      />
    </div>
  );
}
